import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';

import Icon from 'components/DesignSystem/Icon';
import MobileSectionHeader from 'components/Layout/MobileShell/MobileSectionHeader';
import SectionTabs from 'components/Layout/MobileShell/SectionTabs';
import MoreSheet from 'components/Layout/MobileShell/MoreSheet';
import MobileListState from 'components/Layout/MobileShell/MobileListState';
import MobileToolbar from 'components/Layout/MobileShell/MobileToolbar';
import CountersRow from 'components/Admin/Companies/CountersRow';
import DownloadErrorSnackbar from 'components/DownloadErrorSnackbar';
import { onTextChange } from 'helper/controlledField';
import { ListedCompanyContract } from 'helper/companies';
import { CompanyModalFilters, DEFAULT_COMPANY_FILTERS, csvDownloadFilters } from 'helper/companyFilters';
import { ListedProduct } from 'helper/products';
import { closeModal, openModal } from 'helper/modals';
import { downloadCompanyListCsvBlob } from 'services/fileDownloadApi';
import useConfirmationDialogBox from 'utils/useConfirmationDialogBox';
import useFileDownloadApi from 'utils/hooks/useFileDownloadApi';
import ADMIN_SECTION_TABS from 'constants/adminSectionTabs';
import { cssVar } from 'constants/theme';

import CompanyCard from './CompanyCard';
import FilterSheet from './FilterSheet';

import './index.scss';

export default function CompaniesMobile({
  data,
  isLoading,
  isFetching,
  listEnded,
  searchQuery,
  searchBarText,
  setSearchBarText,
  setSearchQuery,
  filters,
  setFilters,
  filterActive,
  appliedFilters,
  productsData,
  productsLoading,
  onEditClick,
  onNewCompanyClick,
  onNewContractClick,
}: {
  data: ListedCompanyContract[] | undefined,
  isLoading: boolean,
  isFetching: boolean,
  listEnded: boolean,
  searchQuery: string,
  searchBarText: string,
  setSearchBarText: React.Dispatch<React.SetStateAction<string>>,
  setSearchQuery: React.Dispatch<React.SetStateAction<string>>,
  filters: CompanyModalFilters,
  setFilters: React.Dispatch<React.SetStateAction<CompanyModalFilters>>,
  filterActive: boolean,
  appliedFilters: React.ReactNode[],
  productsData: ListedProduct[] | undefined,
  productsLoading: boolean,
  onEditClick: (id: number, contractId: number) => () => void,
  onNewCompanyClick: () => void,
  onNewContractClick: () => void,
}) {
  const navigate = useNavigate();
  const [moreOpen, setMoreOpen] = useState(false);
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);
  const [fabMenuOpen, setFabMenuOpen] = useState(false);
  const { confirmationDialogBox, setConfirmationDialogBox } = useConfirmationDialogBox();
  const {
    isLoading: downloadLoading,
    error: downloadError,
    clearError: clearDownloadError,
    onClick: onDownloadClick,
  } = useFileDownloadApi({
    blobDownloader: downloadCompanyListCsvBlob,
    params: csvDownloadFilters(filters, searchQuery),
    filename: 'lista_de_empresas.csv',
  });

  const runFabAction = (action: () => void) => () => {
    setFabMenuOpen(false);
    action();
  };

  const clearAllFilters = () => {
    setFilters(DEFAULT_COMPANY_FILTERS);
    setSearchQuery('');
    setSearchBarText('');
  };

  const resultsCount = data?.length || 0;

  return (
    <div className="companies-mobile">
      <MobileSectionHeader
        backLabel="Administração"
        title="Empresas"
        onBack={() => navigate('/admin')}
        onAvatarClick={() => setMoreOpen(true)}
      />
      <SectionTabs tabs={ADMIN_SECTION_TABS} sheetTitle="Administração" />

      <CountersRow
        className="companies-mobile-counters"
        searchQuery={searchQuery}
        filters={filters}
        setFilters={setFilters}
      />

      <div className="companies-mobile-content">
        <MobileToolbar
          searchValue={searchBarText}
          onSearchChange={onTextChange(setSearchBarText)}
          setSearchQuery={setSearchQuery}
          searchId="companies-mobile-search-bar"
          searchPlaceholder="Pesquisar empresa"
          filterActive={filterActive}
          filterAriaLabel="Filtrar empresas"
          appliedFilters={appliedFilters}
          onFilterClick={openModal(setFilterSheetOpen)}
          onClearAll={clearAllFilters}
          resultsCount={resultsCount}
          resultsNoun="contrato"
          loading={isLoading}
          trailingAction={(
            <button
              type="button"
              aria-label="Baixar empresas filtradas (CSV)"
              className="companies-mobile-download-button"
              onClick={onDownloadClick}
              disabled={downloadLoading}
            >
              {downloadLoading
                ? <Spinner animation="border" size="sm" />
                : <Icon iconType="download" color={cssVar('text-secondary')} size="small" />}
            </button>
          )}
        />

        <MobileListState
          loading={isLoading}
          isEmpty={resultsCount === 0}
          emptyMessage="Nenhuma empresa encontrada."
          infiniteScroll={{ fetchingMore: isFetching, listEnded }}
        >
          {data?.map((company) => (
            <CompanyCard
              key={company.contrato_id || company.empresa_id}
              company={company}
              setConfirmationDialogBox={setConfirmationDialogBox}
              onEditClick={onEditClick}
            />
          ))}
        </MobileListState>
      </div>

      {fabMenuOpen && (
        <button
          type="button"
          aria-label="Fechar menu de criação"
          className="companies-mobile-fab-backdrop"
          onClick={() => setFabMenuOpen(false)}
        />
      )}

      <div className="companies-mobile-fab-area">
        {fabMenuOpen && (
          <div className="companies-mobile-fab-menu">
            <button
              type="button"
              className="companies-mobile-fab-menu-item"
              onClick={runFabAction(onNewCompanyClick)}
            >
              <Icon iconType="add" color={cssVar('text-strong')} size="small" />
              Adicionar empresa
            </button>
            <button
              type="button"
              className="companies-mobile-fab-menu-item"
              onClick={runFabAction(onNewContractClick)}
            >
              <Icon iconType="add" color={cssVar('text-strong')} size="small" />
              Adicionar contrato
            </button>
          </div>
        )}
        <button
          type="button"
          className={`companies-mobile-fab${fabMenuOpen ? ' open' : ''}`}
          aria-expanded={fabMenuOpen}
          onClick={() => setFabMenuOpen(!fabMenuOpen)}
        >
          <Icon iconType={fabMenuOpen ? 'close' : 'add'} color={cssVar('text-on-accent')} size="small" />
          {fabMenuOpen ? 'Fechar' : 'Adicionar'}
        </button>
      </div>

      <DownloadErrorSnackbar message={downloadError} onClose={clearDownloadError} />

      <FilterSheet
        open={filterSheetOpen}
        closeModal={closeModal(setFilterSheetOpen)}
        filters={filters}
        setFilters={setFilters}
        productsLoading={productsLoading}
        products={productsData}
      />

      {confirmationDialogBox}
      <MoreSheet open={moreOpen} dismiss={() => setMoreOpen(false)} />
    </div>
  );
}
